import {
  clone,
  finalMarketingPrice,
  marketingBudget,
  marketingLineItems,
  requiredMarketingState,
  submissionGates,
  submitBase,
  submitMarketingVersion
} from "./release2BState.js";

export const PHOTOGRAPHY_ADDONS = Object.freeze([
  { id: "VUE-AERIAL", name: "VUE Aerial", basePrice: 135 },
  { id: "VIDEO-AERIAL", name: "Video Aerial", basePrice: 175 },
  { id: "ZILLOW-3D", name: "Zillow 3D Home", basePrice: 150 },
  { id: "MATTERPORT-3D", name: "Matterport 3D", basePrice: 250 },
  { id: "2D-FLOOR-PLAN", name: "2D Floor Plan", basePrice: 150 },
  { id: "QUICK-SNAP", name: "Quick Snap", basePrice: 250 },
  { id: "QUICK-SNAP-3", name: "Quick Snap three-pack", basePrice: 500 },
  { id: "GIVE-EM-LITTLE-PROPERTY", name: "Give ’Em a Little, property only", basePrice: 350 },
  { id: "GIVE-EM-LITTLE-AGENT", name: "Give ’Em a Little with agent intro/outro", basePrice: 425 },
  { id: "MAKE-ME-FAMOUS", name: "Make Me Famous", basePrice: 600 },
  { id: "SHOW-STOPPER", name: "Show Stopper", basePrice: 800 }
]);

export const OPTIONAL_MARKETING_CATEGORIES = Object.freeze([
  { id: "TWILIGHT", name: "Twilight Photography" },
  { id: "PAID-SOCIAL", name: "Facebook / Paid Social Advertising" },
  { id: "OPEN-HOUSE-PROMO", name: "Open House Promotion" },
  { id: "ADDITIONAL-POSTCARDS", name: "Additional Postcards" },
  { id: "PRINT-MATERIALS", name: "Print Materials" }
]);

export function approvedOptionalPrice(line) {
  return finalMarketingPrice(line?.basePrice || 0);
}

export function marketingLineItemsComplete(record) {
  const lines = [...marketingLineItems(record)];
  for (const id of record?.photoAddons || []) {
    const item = PHOTOGRAPHY_ADDONS.find(candidate => candidate.id === id);
    if (item) lines.push({ id: `addon-${id}`, label: item.name, amount: finalMarketingPrice(item.basePrice) });
  }
  for (const line of record?.approvedOptionalLineItems || []) {
    if (Number(line?.basePrice) > 0) {
      lines.push({ id: `approved-${line.id || line.name}`, label: line.name || "Approved Marketing Resource", amount: approvedOptionalPrice(line) });
    }
  }
  return lines;
}

export function pendingMarketingRequests(record) {
  const requests = OPTIONAL_MARKETING_CATEGORIES
    .filter(category => record?.optionalResourceRequests?.[category.id])
    .map(category => category.name);
  if (String(record?.otherMarketingRequest || "").trim()) requests.push("Other marketing resource requested");
  return requests;
}

export function marketingAllocationComplete(record) {
  const budget = marketingBudget(record);
  const required = requiredMarketingState(record);
  const allocated = Math.round((marketingLineItemsComplete(record).reduce((sum, line) => sum + Number(line.amount || 0), 0) + Number.EPSILON) * 100) / 100;
  const remaining = budget.cap === null ? null : Math.max(0, Math.round((budget.cap - allocated + Number.EPSILON) * 100) / 100);
  const overage = budget.cap === null ? 0 : Math.max(0, Math.round((allocated - budget.cap + Number.EPSILON) * 100) / 100);
  const overBudget = overage > 0.0001;
  return {
    potential: budget.cap,
    allocated,
    remaining,
    overage,
    overBudget,
    requiredComplete: required.selected,
    requiredCannotFit: required.cannotFit,
    requiredCost: required.requiredCost,
    requiredExceptionApproved: required.exceptionApproved,
    reviewRequired: Boolean(budget.ready && !budget.eligible) || required.cannotFit || overBudget
  };
}

export function canAddPricedOptional(record, item) {
  const allocation = marketingAllocationComplete(record);
  if (!allocation.requiredComplete || allocation.overBudget) return false;
  const price = finalMarketingPrice(item?.basePrice || 0);
  return allocation.remaining === null || price <= allocation.remaining + 0.0001;
}

export function selectionOrderUnlocked(record, key) {
  if (!record?.photoPackageId) return false;
  if (key === "justListed") return true;
  if (key === "justSold") return Boolean(record.requiredMarketing?.justListed);
  if (key === "closingGift") return Boolean(record.requiredMarketing?.justListed && record.requiredMarketing?.justSold);
  return false;
}

export function submissionGatesComplete(record) {
  const gates = submissionGates(record);
  if (record?.kind !== "listing") return gates;
  const allocation = marketingAllocationComplete(record);
  const required = requiredMarketingState(record);
  const marketingBlocks = [...gates.marketingBlocks];
  const marketingLeadership = [...gates.marketingLeadership];
  const oldOverBudget = "Selected Marketing Plan exceeds the Potential Marketing Budget and must be revised";
  const oldException = "Required marketing cannot fit within the Potential Marketing Budget; Leadership exception required";
  const filteredBlocks = marketingBlocks.filter(item => item !== oldOverBudget);
  const filteredLeadership = marketingLeadership.filter(item => item !== oldException);
  if (required.selected && allocation.overBudget) {
    const requiredOnly = required.cannotFit && allocation.allocated <= required.requiredCost + 0.0001;
    if (requiredOnly) {
      if (!required.exceptionApproved) filteredLeadership.push(oldException);
    } else {
      filteredBlocks.push(oldOverBudget);
    }
  }
  return {
    ...gates,
    marketingBlocks: filteredBlocks,
    marketingLeadership: filteredLeadership,
    blocks: [...gates.baseBlocks, ...filteredBlocks],
    leadership: [...gates.baseLeadership, ...filteredLeadership, ...gates.openHouseLeadership]
  };
}

export function canSubmitComplete(record) {
  const gates = submissionGatesComplete(record);
  if (record?.kind === "seller" && !record.linkedListing) return { ok: false, reason: "Select the existing ROOTS Listing or Pre-List record. Seller Pending cannot be created without it." };
  if (record?.status === "Needs Changes") {
    return gates.baseBlocks.length ? { ok: false, reason: "Resolve the Base Transaction Blocks Submission items before resubmitting the base transaction." } : { ok: true };
  }
  return gates.blocks.length ? { ok: false, reason: "Resolve the Blocks Submission items before submitting." } : { ok: true };
}

function appendOptionalExecutionLines(record) {
  const next = clone(record);
  const versionId = next.marketingSubmittedVersionId;
  if (!versionId) return next;
  const existingIds = new Set((next.marketingExecutionLines || []).filter(line => !line.historical && line.marketingVersionId === versionId).map(line => line.catalogItemId));
  const coreIds = new Set(marketingLineItems(next).map(line => line.id));
  for (const item of marketingLineItemsComplete(next)) {
    if (coreIds.has(item.id) || existingIds.has(item.id)) continue;
    const completed = (next.marketingExecutionLines || []).find(line => line.catalogItemId === item.id && line.executionStatus === "Completed" && Math.abs(Number(line.approvedFinalPrice) - Number(item.amount)) < 0.01);
    if (completed) continue;
    next.marketingExecutionLineCounter = Number(next.marketingExecutionLineCounter || 0) + 1;
    next.marketingExecutionLines.push({
      lineId: `ML-${String(next.marketingExecutionLineCounter).padStart(4, "0")}`,
      catalogItemId: item.id,
      label: item.label,
      marketingVersionId: versionId,
      approvedFinalPrice: Number(item.amount),
      executionStatus: "Waiting for Marketing Budget Approval",
      historical: false
    });
  }
  return next;
}

export function submitMarketingVersionComplete(record, source = "Agent submission") {
  return appendOptionalExecutionLines(submitMarketingVersion(record, source));
}

export function submitBaseComplete(record) {
  const validation = canSubmitComplete(record);
  if (!validation.ok) return { record: clone(record), validation };
  const result = submitBase(record);
  if (!result.validation.ok) return result;
  return {
    validation,
    record: result.record?.kind === "listing" && result.record.marketingSubmitted ? appendOptionalExecutionLines(result.record) : result.record
  };
}
